import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { PageHeader, EmptyState } from '../components/SectionHeader';
import { JobCard } from '../components/Cards';
import { CATEGORIES, sortJobs } from '../data/demo';
import { Pagination, usePagination } from '../components/Pagination';
import { useI18n } from '../i18n';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OfertasPage() {
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const [q, setQ] = useState(searchParams.get('q') || '');
  const [loc, setLoc] = useState(searchParams.get('loc') || '');
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const cat = searchParams.get('cat') || 'all';

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/jobs`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : []))
      .then((rows) => setJobs(Array.isArray(rows) ? rows : []))
      .catch(() => setJobs([]))
      .finally(() => setLoading(false));
    const syncApplicants = ({ detail }) => setJobs((rows) => rows.map((job) => job.id === detail.jobId ? { ...job, applicants: detail.applicants } : job));
    window.addEventListener('stagecrew:job-applicants', syncApplicants);
    return () => window.removeEventListener('stagecrew:job-applicants', syncApplicants);
  }, []);
  const isClosed = (j) => j.status && j.status !== 'live';

  const filtered = useMemo(() => {
    const list = sortJobs(jobs.filter((j) => {
      if (cat !== 'all' && j.category !== cat) return false;
      if (q && !(j.title.toLowerCase().includes(q.toLowerCase()) || (j.skills || []).join(' ').toLowerCase().includes(q.toLowerCase()))) return false;
      if (loc && !(j.location || '').toLowerCase().includes(loc.toLowerCase())) return false;
      return true;
    }));
    return [...list.filter((j) => !isClosed(j)), ...list.filter(isClosed)];
  }, [q, loc, cat, jobs]);

  const pagination = usePagination(filtered, 6, `${q}|${loc}|${cat}`);

  const setCat = (id) => {
    const p = new URLSearchParams(searchParams);
    if (id === 'all') p.delete('cat'); else p.set('cat', id);
    setSearchParams(p);
  };

  return (
    <div className="min-h-screen bg-[#0B0C0E]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <PageHeader title={t('nav.ofertas')} subtitle={t('section.latestJobs.sub')} testId="ofertas-header" />

        {/* Filter bar */}
        <div className="sc-card p-4 mb-8 flex flex-col md:flex-row gap-3" data-testid="ofertas-filter-bar">
          <input
            data-testid="ofertas-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('hero.searchPlaceholder')}
            className="bg-[#0B0C0E] border border-[#2A2F3D] focus:border-amber-500 outline-none rounded-lg px-4 py-2.5 text-white flex-1 text-sm"
          />
          <input
            data-testid="ofertas-location-input"
            value={loc}
            onChange={(e) => setLoc(e.target.value)}
            placeholder={t('hero.locationPlaceholder')}
            className="bg-[#0B0C0E] border border-[#2A2F3D] focus:border-amber-500 outline-none rounded-lg px-4 py-2.5 text-white text-sm md:w-56"
          />
        </div>

        {/* Category chips */}
        <div className="flex gap-2 overflow-x-auto pb-3 sc-scrollbar-hide mb-6" data-testid="ofertas-category-filter">
          <button
            onClick={() => setCat('all')}
            data-testid="cat-chip-all"
            className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider rounded-full border whitespace-nowrap transition-colors ${cat === 'all' ? 'bg-amber-500 text-slate-950 border-amber-500' : 'text-slate-300 border-[#2A2F3D] hover:border-amber-500/40'}`}
          >
            Todas
          </button>
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              onClick={() => setCat(c.id)}
              data-testid={`cat-chip-${c.id}`}
              className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider rounded-full border whitespace-nowrap transition-colors ${cat === c.id ? 'bg-amber-500 text-slate-950 border-amber-500' : 'text-slate-300 border-[#2A2F3D] hover:border-amber-500/40'}`}
            >
              {c.name}
            </button>
          ))}
        </div>

        <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-4">
          {filtered.length} {t('meta.results')}
        </div>

        {loading ? (
          <div className="py-24 text-center text-slate-500 font-mono text-sm" data-testid="ofertas-loading">Cargando ofertas…</div>
        ) : filtered.length === 0 ? (
          <EmptyState title="Aún no hay ofertas publicadas" subtitle="Sé el primero en publicar una oferta. Aparecerá aquí al instante." />
        ) : (
          <div data-testid="jobs-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {pagination.items.map((j) => <JobCard key={j.id} job={j} closed={isClosed(j)} />)}
          </div>
        )}
        <Pagination {...pagination} testId="ofertas" />
      </div>
      <Footer />
    </div>
  );
}
